import type { ReactNode } from 'react'
import type { CollageTemplate } from '@/content/collageTemplates'
import { CollageFamily } from '@/content/collageTemplates'
import type { FilmFormat } from '@/content/instantFilm'
import type { AspectRatio } from '@/core/geometry'
import { CollageBackgroundKind, SplitKind } from '@/core/document'
import { ExportFormat, ResizeMode } from '@/core/export/types'
import { PhotoPlacement } from '@/tools/frame/params'
import { PhotoGlyph } from '@/ui/illustrations/PhotoGlyph'
import { PASTEL, PASTEL_ORDER, PastelId } from '@/ui/illustrations/palette'

function IllustrationSvg({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className ?? 'size-full'} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  )
}

export function PolaroidIllustration() {
  return (
    <IllustrationSvg>
      <rect x="9" y="4" width="30" height="40" rx="2.5" fill="white" className="dark:fill-neutral-100" />
      <PhotoGlyph x={12} y={8} width={24} height={24} color={PastelId.Peach} radius={1.5} />
      <path d="M15 38c4-2 8-2 12 0 2 1 4 1 6 0" />
    </IllustrationSvg>
  )
}

export function FilmFormatIllustration({ format }: { format: FilmFormat }) {
  const maxWidth = 34
  const maxHeight = 40
  const scale = Math.min(maxWidth / format.filmMm.width, maxHeight / format.filmMm.height)
  const width = format.filmMm.width * scale
  const height = format.filmMm.height * scale
  const x = (48 - width) / 2
  const y = (48 - height) / 2
  return (
    <IllustrationSvg>
      <rect x={x} y={y} width={width} height={height} rx="2" fill="white" className="dark:fill-neutral-100" />
      <PhotoGlyph
        x={x + format.windowMm.x * scale}
        y={y + format.windowMm.y * scale}
        width={format.windowMm.width * scale}
        height={format.windowMm.height * scale}
        color={PastelId.Mint}
        radius={1}
      />
    </IllustrationSvg>
  )
}

export function PlacementIllustration({ placement }: { placement: PhotoPlacement }) {
  const inset = placement === PhotoPlacement.Fill ? 0 : 5
  return (
    <IllustrationSvg>
      <rect x="7" y="5" width="34" height="38" rx="2.5" fill="white" className="dark:fill-neutral-100" />
      <rect x="11" y="9" width="26" height="25" rx="1.5" fill="none" />
      <PhotoGlyph x={11 + inset} y={9 + inset} width={26 - inset * 2} height={25 - inset * 2} color={PastelId.Sky} radius={1} />
    </IllustrationSvg>
  )
}

export type ShadowIllustrationKind = 'inside' | 'behind'

export function ShadowIllustration({ kind, enabled }: { kind: ShadowIllustrationKind; enabled: boolean }) {
  return (
    <IllustrationSvg>
      {kind === 'behind' && enabled ? <rect x="11" y="9" width="28" height="34" rx="2" fill="currentColor" opacity="0.18" stroke="none" /> : null}
      <rect x="8" y="5" width="30" height="36" rx="2" fill="white" className="dark:fill-neutral-100" />
      <PhotoGlyph x={12} y={9} width={22} height={22} color={PastelId.Lavender} radius={1} />
      {kind === 'inside' && enabled ? <path d="M12 9h22v22H12z" strokeWidth="3" opacity="0.28" /> : null}
    </IllustrationSvg>
  )
}

export type DateIllustrationKind = 'written' | 'stamp' | 'caption'

export function DateIllustration({ kind, enabled }: { kind: DateIllustrationKind; enabled: boolean }) {
  return (
    <IllustrationSvg>
      <rect x="7" y="5" width="34" height="38" rx="2" fill="white" className="dark:fill-neutral-100" />
      <PhotoGlyph x={11} y={9} width={26} height={23} color={PastelId.Butter} radius={1} />
      {kind === 'written' ? <path d="M14 37c3-2 5 2 8 0s5 2 8 0" opacity={enabled ? 1 : 0.28} /> : null}
      {kind === 'stamp' ? <text x="14" y="28" fill="currentColor" stroke="none" fontSize="5" fontFamily="monospace" opacity={enabled ? 1 : 0.28}>26 08 28</text> : null}
      {kind === 'caption' ? <path d="M13 37h19M16 40h13" opacity={enabled ? 1 : 0.28} /> : null}
    </IllustrationSvg>
  )
}

export function CollageFamilyIllustration({ family }: { family: CollageFamily }) {
  const prints = family === CollageFamily.Prints
  return (
    <IllustrationSvg>
      {[0, 1, 2, 3].map((index) => {
        const x = prints ? [7, 24, 10, 27][index] : 5 + index % 2 * 20
        const y = prints ? [7, 9, 25, 24][index] : 5 + Math.floor(index / 2) * 20
        if (x === undefined || y === undefined) throw new Error('Collage family illustration position is missing')
        const rotation = prints ? [-6, 5, 4, -5][index] : 0
        return (
          <g key={index} transform={`rotate(${rotation ?? 0} ${x + 8} ${y + 8})`}>
            {prints ? <rect x={x - 1.5} y={y - 1.5} width="19" height="21" rx="1.5" fill="white" className="dark:fill-neutral-100" /> : null}
            <PhotoGlyph x={x} y={y} width={prints ? 16 : 18} height={prints ? 16 : 18} color={PASTEL_ORDER[index] ?? PastelId.Peach} radius={1.5} />
          </g>
        )
      })}
    </IllustrationSvg>
  )
}

export function CollageTemplateIllustration({ template, ratio }: { template: CollageTemplate; ratio: AspectRatio }) {
  const maxWidth = 42
  const maxHeight = 42
  const scale = Math.min(maxWidth / ratio.w, maxHeight / ratio.h)
  const width = ratio.w * scale
  const height = ratio.h * scale
  const originX = (48 - width) / 2
  const originY = (48 - height) / 2
  return (
    <IllustrationSvg>
      <rect x={originX} y={originY} width={width} height={height} rx="2" fill="currentColor" opacity="0.06" />
      {template.cells.map((cell, index) => {
        const x = originX + cell.rect.x * width
        const y = originY + cell.rect.y * height
        const cellWidth = cell.rect.width * width
        const cellHeight = cell.rect.height * height
        const prints = template.family === CollageFamily.Prints
        return (
          <g key={cell.z} transform={`rotate(${cell.rotationDeg} ${x + cellWidth / 2} ${y + cellHeight / 2})`}>
            {prints ? <rect x={x - 1.2} y={y - 1.2} width={cellWidth + 2.4} height={cellHeight + 3.5} rx="1" fill="white" className="dark:fill-neutral-100" /> : null}
            <PhotoGlyph x={x + 0.7} y={y + 0.7} width={Math.max(1, cellWidth - 1.4)} height={Math.max(1, cellHeight - 1.4)} color={PASTEL_ORDER[index % PASTEL_ORDER.length] ?? PastelId.Peach} radius={0.8} />
          </g>
        )
      })}
    </IllustrationSvg>
  )
}

export function RatioBadge({ ratio }: { ratio: AspectRatio }) {
  const scale = Math.min(10 / ratio.w, 10 / ratio.h)
  return (
    <svg viewBox="0 0 12 12" className="size-3" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
      <rect x={(12 - ratio.w * scale) / 2} y={(12 - ratio.h * scale) / 2} width={ratio.w * scale} height={ratio.h * scale} rx="1" />
    </svg>
  )
}

export function ShapeIllustration({ ratio }: { ratio: AspectRatio }) {
  const scale = Math.min(36 / ratio.w, 36 / ratio.h)
  const width = ratio.w * scale
  const height = ratio.h * scale
  const x = (48 - width) / 2
  const y = (48 - height) / 2
  return (
    <IllustrationSvg>
      <rect x={x} y={y} width={width} height={height} rx="2.5" fill="none" />
      <PhotoGlyph x={x + 2} y={y + 2} width={Math.max(2, width - 4)} height={Math.max(2, height - 4)} color={PastelId.Rose} radius={1.5} />
    </IllustrationSvg>
  )
}

export type SplitIllustrationKind = 'single' | SplitKind

export function SplitIllustration({ kind }: { kind: SplitIllustrationKind }) {
  return (
    <IllustrationSvg>
      {kind === 'single' ? <PhotoGlyph x={8} y={6} width={32} height={36} color={PastelId.Sky} /> : null}
      {kind === SplitKind.Carousel ? (
        <>
          {[5, 17, 29].map((x, index) => <PhotoGlyph key={x} x={x} y={8} width={12} height={29} color={PASTEL_ORDER[index] ?? PastelId.Peach} radius={1.5} />)}
          <circle cx="21" cy="42" r="1" fill="currentColor" /><circle cx="25" cy="42" r="1" fill="currentColor" opacity="0.3" />
        </>
      ) : null}
      {kind === SplitKind.Grid ? Array.from({ length: 6 }, (_, index) => (
        <PhotoGlyph key={index} x={7 + index % 3 * 12} y={8 + Math.floor(index / 3) * 17} width={10} height={15} color={PASTEL_ORDER[index] ?? PastelId.Peach} radius={1} />
      )) : null}
    </IllustrationSvg>
  )
}

export function FileIllustration({ format }: { format: ExportFormat }) {
  return (
    <IllustrationSvg>
      <PhotoGlyph x={7} y={6} width={34} height={36} color={format === ExportFormat.Jpeg ? PastelId.Peach : PastelId.Mint} />
      {format === ExportFormat.Png ? (
        <path d="M31 32h10v10H31zM31 32l10 10M41 32 31 42" fill="white" className="dark:fill-neutral-100" />
      ) : null}
      <text x="24" y="39" textAnchor="middle" fill="currentColor" stroke="none" fontSize="6" fontWeight="700">{format === ExportFormat.Jpeg ? 'JPG' : 'PNG'}</text>
    </IllustrationSvg>
  )
}

export function SizeIllustration({ mode }: { mode: ResizeMode }) {
  return (
    <IllustrationSvg>
      {mode === ResizeMode.InstagramTarget ? (
        <><rect x="13" y="3" width="22" height="42" rx="5" /><PhotoGlyph x={16} y={9} width={16} height={27} color={PastelId.Rose} radius={1.5} /><circle cx="24" cy="41" r="1" /></>
      ) : null}
      {mode === ResizeMode.Original ? (
        <>{Array.from({ length: 16 }, (_, index) => <rect key={index} x={7 + index % 4 * 9} y={7 + Math.floor(index / 4) * 9} width="7" height="7" fill={PASTEL[PASTEL_ORDER[index % PASTEL_ORDER.length] ?? PastelId.Peach]} className="dark:fill-opacity-[0.85]" />)}</>
      ) : null}
      {mode === ResizeMode.Custom ? (
        <><path d="M6 34 34 6l8 8-28 28z" fill={PASTEL[PastelId.Butter]} className="dark:fill-opacity-[0.85]" /><path d="m14 30-3-3m7-1-3-3m7-1-3-3m7-1-3-3m7-1-3-3" /></>
      ) : null}
    </IllustrationSvg>
  )
}

export function BackgroundIllustration({ kind }: { kind: CollageBackgroundKind }) {
  return (
    <IllustrationSvg>
      {kind === CollageBackgroundKind.Color ? <rect x="6" y="6" width="36" height="36" rx="6" fill={PASTEL[PastelId.Peach]} className="dark:fill-opacity-[0.85]" /> : null}
      {kind === CollageBackgroundKind.Gradient ? (
        <><path d="M6 12a6 6 0 0 1 6-6h12v36H12a6 6 0 0 1-6-6z" fill={PASTEL[PastelId.Sky]} className="dark:fill-opacity-[0.85]" /><path d="M24 6h12a6 6 0 0 1 6 6v24a6 6 0 0 1-6 6H24z" fill={PASTEL[PastelId.Lavender]} className="dark:fill-opacity-[0.85]" /></>
      ) : null}
      {kind === CollageBackgroundKind.Image ? <><PhotoGlyph x={6} y={6} width={36} height={36} color={PastelId.Mint} /><path d="M2 15h7M39 24h7M2 33h7" opacity="0.45" /></> : null}
    </IllustrationSvg>
  )
}

export function ColorWheelIllustration() {
  return (
    <IllustrationSvg>
      {PASTEL_ORDER.map((color, index) => {
        const angle = index / PASTEL_ORDER.length * Math.PI * 2
        return <circle key={color} cx={24 + Math.cos(angle) * 10} cy={24 + Math.sin(angle) * 10} r="7" fill={PASTEL[color]} stroke="none" className="dark:fill-opacity-[0.85]" />
      })}
      <circle cx="24" cy="24" r="5" fill="white" className="dark:fill-background" />
    </IllustrationSvg>
  )
}
